import React, { createContext, useContext, useEffect, useState } from "react";
import { BBG_REPORT_API, TPDM_FILTERS_API } from "../config/apiConfig";
import { securedFetch } from "../utils/api";
import { allOptionId } from "../utils/constants";
import { extractAndSortFieldsFromCategories } from "../utils/utils";

const BloombergContext = createContext();

export const BloombergProvider = ({ children }) => {
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState(null);

  const [bloombergFilters, setBloombergFilters] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [tickers, setTickers] = useState([]);
  const [selectedTicker, setSelectedTicker] = useState(null);

  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);

  const [selectedPresetDateRange, setSelectedPresetDateRange] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const [bloombergData, setBloombergData] = useState([]);
  const [bloombergDataLoading, setBloombergDataLoading] = useState(false);
  const [bloombergDataError, setBloombergDataError] = useState(null);

  useEffect(() => {
    setFiltersLoading(true);
    setFiltersError(null);
    securedFetch(TPDM_FILTERS_API, {
      method: "GET",
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch TPDM filters data");
        }
        return res.json();
      })
      .then((data) => {
        setBloombergFilters(data);
        const { categories } = data || {};
        setCategories(categories || []);
      })
      .catch((error) => {
        setFiltersError(error.message);
      })
      .finally(() => {
        setFiltersLoading(false);
      });
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      const categoryObj = (categories || []).find(
        ({ category }) => category === selectedCategory,
      );
      const { tickers } = categoryObj || {};
      setTickers(tickers || []);
      setSelectedTicker(null);
      setSelectedField(null);
    }
  }, [categories, selectedCategory]);

  useEffect(() => {
    if (selectedTicker) {
      if (selectedTicker === allOptionId) {
        const flatternedFields = extractAndSortFieldsFromCategories(categories);
        setFields(flatternedFields);
      } else {
        const tickerObj = (tickers || []).find(
          ({ ticker }) => ticker === selectedTicker,
        );
        const { fields } = tickerObj || {};
        setFields(fields || []);
      }
      setSelectedField(null);
    }
  }, [categories, selectedTicker, tickers]);

  useEffect(() => {
    if (
      selectedCategory &&
      selectedTicker &&
      selectedField &&
      selectedStartDate &&
      selectedEndDate
    ) {
      setBloombergDataLoading(true);
      setBloombergDataError(null);
      securedFetch(BBG_REPORT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ticker: selectedTicker,
          category: selectedCategory,
          field: selectedField,
          startDateRange: selectedStartDate?.format("YYYY-MM-DD"),
          endDateRange: selectedEndDate?.format("YYYY-MM-DD"),
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to fetch TPDM Bloomberg report data");
          }
          return res.json();
        })
        .then((data) => {
          setBloombergData(data);
        })
        .catch((error) => {
          setBloombergDataError(error.message);
        })
        .finally(() => {
          setBloombergDataLoading(false);
        });
    }
  }, [
    selectedTicker,
    selectedField,
    selectedStartDate,
    selectedEndDate,
    selectedCategory,
  ]);

  return (
    <BloombergContext.Provider
      value={{
        filtersLoading,
        filtersError,

        bloombergFilters,

        categories,
        setCategories,
        selectedCategory,
        setSelectedCategory,

        tickers,
        setTickers,
        selectedTicker,
        setSelectedTicker,

        fields,
        setFields,
        selectedField,
        setSelectedField,

        selectedPresetDateRange,
        setSelectedPresetDateRange,
        selectedStartDate,
        setSelectedStartDate,
        selectedEndDate,
        setSelectedEndDate,

        bloombergData,
        setBloombergData,
        bloombergDataLoading,
        bloombergDataError,
      }}
    >
      {children}
    </BloombergContext.Provider>
  );
};

export const useBloombergContext = () => useContext(BloombergContext);
